import { Badge, type BadgeTone } from '@/components/ui/badge';
import type { TenantDisplayStatus, UserStatus } from '@/types/domain';

/**
 * Maps a domain status onto a tone and a human label in one place, so the same
 * status never renders two different ways across pages.
 */
const TENANT_TONES: Record<TenantDisplayStatus, { tone: BadgeTone; label: string }> = {
  ACTIVE: { tone: 'success', label: 'Active' },
  EXPIRING_SOON: { tone: 'warning', label: 'Expiring soon' },
  EXPIRED: { tone: 'danger', label: 'Expired' },
  INACTIVE: { tone: 'neutral', label: 'Inactive' },
};

const USER_TONES: Record<UserStatus, { tone: BadgeTone; label: string }> = {
  ACTIVE: { tone: 'success', label: 'Active' },
  INACTIVE: { tone: 'neutral', label: 'Inactive' },
  LOCKED: { tone: 'warning', label: 'Locked' },
};

export function TenantStatusBadge({ status }: { status: TenantDisplayStatus }) {
  const { tone, label } = TENANT_TONES[status];

  return (
    <Badge tone={tone} dot>
      {label}
    </Badge>
  );
}

export function UserStatusBadge({ status }: { status: UserStatus }) {
  const { tone, label } = USER_TONES[status];

  return (
    <Badge tone={tone} dot>
      {label}
    </Badge>
  );
}

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Superuser',
  TENANT_ADMIN: 'Admin',
  TENANT_CLIENT: 'Client',
};

export function RoleBadge({ role }: { role: string }) {
  return (
    <Badge
      tone={role === 'SUPER_ADMIN' ? 'primary' : role === 'TENANT_ADMIN' ? 'accent' : 'neutral'}
    >
      {ROLE_LABELS[role] ?? role}
    </Badge>
  );
}
